import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Clock, Phone, MapPin, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitingList = () => {
  const [waitingList, setWaitingList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWaitingList();
  }, []);

  const fetchWaitingList = async () => {
    try {
      const response = await axios.get(`${API}/admin/waiting-list`);
      setWaitingList(response.data);
    } catch (error) {
      console.error("Failed to fetch waiting list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getResultBadge = (result) => {
    const variants = {
      callback: { class: "badge-info", label: "Callback" },
      reschedule: { class: "badge-warning", label: "Reschedule" },
      waiting: { class: "bg-secondary text-muted-foreground", label: "Waiting" },
    };
    const variant = variants[result] || { class: "bg-secondary", label: result || "Pending" };
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-sm ${variant.class}`}>
        {variant.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="waiting-list-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Chivo']">Waiting List</h1>
        <p className="text-muted-foreground">
          Leads requiring follow-up, callbacks, or rescheduling
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-primary/20">
                <Clock size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Waiting</p>
                <p className="text-2xl font-bold text-foreground font-['Chivo']">
                  {waitingList.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-secondary">
                <Phone size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Callbacks</p>
                <p className="text-2xl font-bold text-foreground font-['Chivo']">
                  {waitingList.filter((l) => l.call_result === "callback").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-secondary">
                <FileText size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Reschedules</p>
                <p className="text-2xl font-bold text-foreground font-['Chivo']">
                  {waitingList.filter((l) => l.call_result === "reschedule").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Waiting List Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-['Chivo']">Pending Follow-ups</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Date
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Customer
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Caller
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Contact
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Status
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Notes
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {waitingList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      <Clock size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No pending follow-ups</p>
                      <p className="text-sm mt-1">
                        Callbacks and reschedules will appear here
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  waitingList.map((item) => (
                    <TableRow key={item.id} className="hover:bg-secondary/20">
                      <TableCell className="font-mono text-sm">
                        {item.date_of_call}
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.customer_name || "N/A"}
                      </TableCell>
                      <TableCell>{item.caller_name || "N/A"}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {item.ho_manager_name && (
                            <p className="text-sm">{item.ho_manager_name}</p>
                          )}
                          {item.phone_number && (
                            <p className="text-xs text-muted-foreground font-mono">
                              {item.phone_number}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getResultBadge(item.call_result)}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-muted-foreground truncate">
                          {item.additional_notes || "-"}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitingList;
